import React, { useEffect, useRef, useState } from 'react';
import L, { LeafletMouseEvent } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faXmark } from '@fortawesome/pro-solid-svg-icons';

export function toGoogleMapsLink(address: string): string {
  const baseUrl = "https://www.google.com/maps/search/?api=1&query=";
  const encodedAddress = encodeURIComponent(address);
  return baseUrl + encodedAddress;
}

interface NominatimResponse {
  display_name?: string;
  lat?: string;
  lon?: string;
  [key: string]: any;
}

interface Location {
  lat: number;
  lng: number;
}

interface LeafletMapProps {
  onChange?: (data: { locationName: string }) => void;
  initialLocation?: string;
  setBusy: (busy: boolean) => void;
  minZoom?: number;
}

const LeafletMap: React.FC<LeafletMapProps> = ({
  onChange,
  initialLocation,
  setBusy,
  minZoom = 3
}) => {
  const { t, i18n } = useTranslation();
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const mapInitializedRef = useRef(false);
  const [initialLocationSet, setInitialLocationSet] = useState(false);
  const [_isBusy, setIsBusy] = useState(false);

  const getAccentColor = () => {
    return getComputedStyle(document.documentElement).getPropertyValue('--color-accent').trim() || '#ea2d37'; // #ea2d37 = default digipas brand color
  };

  const createCustomIcon = () => {
    const accentColor = getAccentColor();

    return L.icon({
      iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg width="32" height="45" viewBox="0 0 32 45" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.163 0 0 7.163 0 16C0 28 16 45 16 45S32 28 32 16C32 7.163 24.837 0 16 0Z" fill="${accentColor}"/>
          <circle cx="16" cy="16" r="8" fill="white"/>
        </svg>
      `),
      iconSize: [32, 45],
      iconAnchor: [16, 45],

      shadowUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg width="41" height="41" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
          <ellipse cx="20.5" cy="20.5" rx="20.5" ry="20.5" fill="black" fill-opacity="0.2"/>
        </svg>
      `),
      shadowSize: [15, 15],
      shadowAnchor: [8, 8],

      popupAnchor: [0, -45]
    });
  };

  const getCurrentLanguage = (): string => {
    return ['de', 'en', 'fr', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en';
  };

  const getCountryFromLanguage = (language: string): string => {
    const languageToCountry: Record<string, string> = {
      'en': 'United Kingdom',
      'de': 'Germany',
      'fr': 'France',
      'es': 'Spain',
      'nl': 'Netherlands'
    };

    return languageToCountry[language] || 'United Kingdom';
  };

  const removeExistingMarker = () => {
    if (markerRef.current && mapRef.current) {
      mapRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  };

  const createMarker = (lat: number, lng: number): L.Marker | null => {
    if (!mapRef.current) return null;

    const customIcon = createCustomIcon();
    return L.marker([lat, lng], { icon: customIcon }).addTo(mapRef.current);
  };

  const addMarker = (lat: number, lng: number, locationName: string) => {
    removeExistingMarker();
    markerRef.current = createMarker(lat, lng);
    notifyLocationChange(locationName);
    closeMap(500);
  };

  const setMapBusy = (busy: boolean) => {
    setBusy(busy);
    setIsBusy(busy);
  };

  const closeMap = (delay: number = 0) => {
    setTimeout(() => {
      setMapBusy(false);
    }, delay);
  };

  const notifyLocationChange = (locationName: string) => {
    if (onChange) {
      onChange({ locationName });
    }
  };

  const fetchFromNominatim = async (url: string): Promise<NominatimResponse[]> => {
    try {
      const res = await fetch(url);
      return await res.json();
    } catch (err) {
      console.error('Failed to fetch from Nominatim:', err);
      return [];
    }
  };

  const reverseGeocode = async (location: Location): Promise<string> => {
    const { lat, lng } = location;
    const lang = getCurrentLanguage();
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=${lang}`;

    try {
      const response = await fetch(url);
      const data: NominatimResponse = await response.json();

      return data?.display_name || t('map:locationUnknown') as string;
    } catch (error) {
      console.error('Geocoding error:', error);
      return t('map:locationError') as string;
    }
  };

  const geocodeAddress = async (address: string, setAsBusy = true): Promise<boolean> => {
    if (!mapRef.current) return false;

    if (setAsBusy) setMapBusy(true);

    const lang = getCurrentLanguage();
    const encodedAddress = encodeURIComponent(address);
    const url = `https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1&accept-language=${lang}`;

    const results = await fetchFromNominatim(url);

    if (results.length > 0 && results[0].lat && results[0].lon) {
      const lat = parseFloat(results[0].lat);
      const lng = parseFloat(results[0].lon);

      mapRef.current.setView([lat, lng], 15);

      if (setAsBusy) {
        addMarker(lat, lng, results[0].display_name || address);
        return true;
      }

      removeExistingMarker();
      markerRef.current = createMarker(lat, lng);

      return true;
    }

    if (setAsBusy) {
      closeMap(300);
    }

    return false;
  };

  const locateCountry = async () => {
    if (!mapRef.current) return;

    const lang = getCurrentLanguage();
    const country = getCountryFromLanguage(lang);
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(country)}&format=json&limit=1`;

    const results = await fetchFromNominatim(url);

    if (results.length > 0 && results[0].lat && results[0].lon) {
      mapRef.current.setView([parseFloat(results[0].lat), parseFloat(results[0].lon)], 6);
      return;
    }

    setDefaultView();
  };

  const setDefaultView = () => {
    if (!mapRef.current) return;

    mapRef.current.setView([51.505, -0.09], 13);
  };

  const handleMapClick = async (e: LeafletMouseEvent) => {
    setMapBusy(true);

    const { lat, lng } = e.latlng;

    removeExistingMarker();
    markerRef.current = createMarker(lat, lng);

    const locationName = await reverseGeocode({ lat, lng });
    notifyLocationChange(locationName);
    closeMap(500);
  };

  const initializeMap = () => {
    if (mapInitializedRef.current) return;

    const worldBounds = L.latLngBounds(
      L.latLng(-85, -180),
      L.latLng(85, 180)
    );

    mapRef.current = L.map('map', {
      minZoom,
      zoomControl: true,
      maxBounds: worldBounds,
      maxBoundsViscosity: 1.0
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://openstreetmap.org" target="_blank">OpenStreetMap</a>',
    }).addTo(mapRef.current);

    mapRef.current.on('zoomend', () => {
      if (mapRef.current && mapRef.current.getZoom() < minZoom) {
        mapRef.current.setZoom(minZoom);
      }
    });

    mapInitializedRef.current = true;
  };

  const initializeLocation = async () => {
    if (!mapRef.current || initialLocationSet) return;

    if (initialLocation) {
      const success = await geocodeAddress(initialLocation, false);

      if (success) return setInitialLocationSet(true);
    }

    await locateCountry();
    setInitialLocationSet(true);
  };

  useEffect(() => {
    initializeMap();

    return () => {
      if (!mapRef.current) return;

      mapRef.current.remove();
      mapRef.current = null;
      mapInitializedRef.current = false;
      setInitialLocationSet(false);
    };
  }, [minZoom]);

  useEffect(() => {
    initializeLocation();
  }, [initialLocation, initialLocationSet]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.on('click', handleMapClick);

    return () => {
      map.off('click', handleMapClick);
    };
  }, [onChange, setBusy]);

  return (
    // Prevent map from overlaying other elements by setting the parent div's z-index to 0
    <div className='w-full relative mb-6 z-0'>
      <div className='absolute z-[30] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-2xl animate-spin"
        />
      </div>

      <div
        id="map"
        className='w-full h-[400px] rounded-[0.4rem]'
      />
    </div>
  );
};

export default LeafletMap;
